import type { MessageListType, ThreadId } from '../../../types';
import { MAIN_THREAD_ID } from '../../../api/types';

export function shouldForceReloadSparseHistory(params: {
  chatId: string;
  threadId?: ThreadId;
  currentChatId?: string;
  currentThreadId?: ThreadId;
  currentType?: MessageListType;
  nextType?: MessageListType;
  listedIds?: number[];
  loadedMessageCount: number;
  chatLastMessageId?: number;
}) {
  const threadId = params.threadId ?? MAIN_THREAD_ID;
  const nextType = params.nextType ?? 'thread';
  if (threadId !== MAIN_THREAD_ID) return false;
  if (nextType !== 'thread') return false;

  const listedCount = params.listedIds?.length ?? 0;
  const lastListedId = listedCount ? params.listedIds![listedCount - 1] : undefined;
  const isBehindChatSummary = Boolean(
    params.chatLastMessageId && (!lastListedId || lastListedId < params.chatLastMessageId),
  );
  if (isBehindChatSummary) return true;

  return listedCount <= 1 && params.loadedMessageCount <= 1;
}

export function shouldForceReloadLaggingViewport(params: {
  chatId: string;
  threadId?: ThreadId;
  currentChatId?: string;
  currentThreadId?: ThreadId;
  currentType?: MessageListType;
  nextType?: MessageListType;
  listedIds?: number[];
  viewportIds?: number[];
}) {
  const threadId = params.threadId ?? MAIN_THREAD_ID;
  const currentType = params.currentType ?? 'thread';
  const nextType = params.nextType ?? currentType;
  if (currentType !== 'thread' || nextType !== 'thread') return false;
  if (params.currentChatId !== params.chatId || params.currentThreadId !== threadId) return false;

  const listedCount = params.listedIds?.length ?? 0;
  const viewportCount = params.viewportIds?.length ?? 0;
  if (!listedCount || !viewportCount) return false;

  return params.viewportIds![viewportCount - 1] < params.listedIds![listedCount - 1];
}

export function shouldForceReloadUnhydratedPrivateHistory(params: {
  isPrivateChat: boolean;
  threadId?: ThreadId;
  listedIds?: number[];
  hasThreadInfo: boolean;
}) {
  const threadId = params.threadId ?? MAIN_THREAD_ID;
  if (!params.isPrivateChat) return false;
  if (threadId !== MAIN_THREAD_ID) return false;

  return Boolean(params.listedIds?.length) && !params.hasThreadInfo;
}

export function shouldForceReloadMissingReactions(params: {
  isPrivateChat: boolean;
  messages: Array<{ id: number; reactions?: { results?: unknown[] } }>;
}) {
  if (!params.isPrivateChat) return false;
  if (!params.messages.length) return false;

  return params.messages.some((message) => !message.reactions);
}

type ReactionSnapshot = {
  results?: unknown[];
};

type ReactionMessage = {
  id: number;
  reactions?: ReactionSnapshot;
  content?: {
    action?: unknown;
  };
};

type ExistingReactionMessage = {
  reactions?: ReactionSnapshot;
};

export function collectMessageIdsNeedingReactionReload(params: {
  isPrivateChat: boolean;
  messages: ReactionMessage[];
  existingById?: Record<number, ExistingReactionMessage | undefined>;
}) {
  if (!params.isPrivateChat || !params.messages.length) {
    return [];
  }

  return params.messages
    .filter((message) => !message.content?.action)
    .filter((message) => {
      if (!message.reactions) {
        return true;
      }

      const existingResults = params.existingById?.[message.id]?.reactions?.results;
      return !message.reactions.results?.length && !!existingResults && !existingResults.length;
    })
    .map((message) => message.id);
}

type PendingReactionLoad = {
  chatId: string;
  ids: number[];
};

const pendingReactionLoads = new Map<string, Set<number>>();

export function queuePendingMessageReactionLoad(payload: PendingReactionLoad) {
  const ids = pendingReactionLoads.get(payload.chatId) || new Set<number>();
  payload.ids.forEach((id) => ids.add(id));
  pendingReactionLoads.set(payload.chatId, ids);
}

export function drainPendingMessageReactionLoads(): PendingReactionLoad[] {
  const drained = [...pendingReactionLoads.entries()].map(([chatId, ids]) => ({
    chatId,
    ids: [...ids],
  }));
  pendingReactionLoads.clear();
  return drained;
}
